import React, { useMemo, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import api from "../utils/api";
import { logInWithEmailAndPassword, registerWithEmail } from "../utils/authService";
import UserContext from "../context/UserContext";
import "../assets/styles/AuthPage.css";

// add these (you referenced them in your JSX):
import BloomPal_Logo from "../assets/images/BloomPal_MainLogo.png";
import Blossom from "../assets/images/Blossom.webp";

export default function AuthPage({ defaultPhase = "email", defaultEmail = "" }) {
  const navigate = useNavigate();
  const { setName, setReason, setAuthToken, sessionId, visitorId } = useContext(UserContext);

  const [email, setEmail] = useState(defaultEmail);
  const [phase, setPhase] = useState(defaultPhase); // "email" | "login" | "signup"
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // read either key; always send session_id to backend
  //const sessionId = useMemo(
  //  () => localStorage.getItem("session_id") || localStorage.getItem("sessionId") || "",
  //  []
  //);
  //const visitorId = useMemo(() => localStorage.getItem("visitorId") || "", []);

  const errorMessageFrom = (e, fallback) =>
    e?.response?.data?.non_field_errors?.[0] ||
    e?.response?.data?.email?.[0] ||
    e?.response?.data?.password?.[0] ||
    e?.response?.data?.password1?.[0] ||
    e?.response?.data?.password2?.[0] ||
    e?.response?.data?.detail ||
    e?.message ||
    fallback;

  const ensureCsrf = async () => { try { await api.get("/set-csrf/"); } catch {} };

  const fetchUserProfile = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    try {
      const res = await api.get("/api/auth/user/", {
        headers: { Authorization: `Token ${token}` },
        withCredentials: true,
      });
      setName(res.data?.name || "");
      setReason(res.data?.reason || "");
    } catch (e) {
      console.error("Profile fetch failed:", e);
    }
  };

  // If already authenticated, hydrate context then go home
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    (async () => {
      await fetchUserProfile();
      navigate("/");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // —— Google ——
  const onGoogleSuccess = async (cred) => {
    setErr(""); setLoading(true);
    try {
      await ensureCsrf();
      const name = localStorage.getItem("guestName") || "";
      const reason = localStorage.getItem("userReason") || "";
      const { data } = await api.post(
        "/api/auth/google/",
        { id_token: cred.credential, visitor_id: visitorId, session_id: sessionId, name, reason },
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );
      localStorage.setItem("authToken", data.key);
      setAuthToken(data.key);
      await fetchUserProfile();
      navigate("/");
    } catch (e) {
      setErr(errorMessageFrom(e, "Google sign-in failed."));
    } finally {
      setLoading(false);
    }
  };

  // —— Email flow ——
  const handleContinueWithEmail = () => {
    if (!email) { setErr("Please enter your email."); return; }
    setErr("");
    setPhase("login"); // default; user can switch to signup
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await ensureCsrf();
      await logInWithEmailAndPassword(email, password, visitorId, sessionId);
      await fetchUserProfile();
      navigate("/");
    } catch (e2) {
      setErr(errorMessageFrom(e2, "Unable to log in with provided credentials."));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!accept) return setErr("Please agree to the Terms and Privacy Policy.");
    if (password !== password2) return setErr("Passwords do not match.");
    setErr(""); setLoading(true);
    try {
      await ensureCsrf();
      await registerWithEmail(email, password, password2, visitorId, null, sessionId);
      await fetchUserProfile();
      navigate("/"); // or a "check your inbox" page if verification is mandatory
    } catch (e2) {
      setErr(errorMessageFrom(e2, "Sign up failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <img src={BloomPal_Logo} alt="BloomPal Logo" className="login-logo" />

      <div className="login-content">
        {/* Left side — mascot */}
        <div className="login-left">
          <img src={Blossom} alt="Mascot" className="mascot" />
          <h1 className="welcome-text">Welcome back!</h1>
        </div>

        {/* Right side — auth card */}
        <div className="login-right">
          <div className="auth-card" role="dialog" aria-labelledby="auth-title">
            {/* Google */}
            <div className="google-wrap">
              <GoogleLogin
                onSuccess={onGoogleSuccess}
                onError={() => setErr("Google sign-in failed.")}
                theme="outline"
                shape="pill"
                size="large"
                text="continue_with"
                //width="100%"
                useOneTap={phase === "login"}
              />
            </div>

            <div className="divider"><span>OR</span></div>

            {/* Email + CTA (NO NESTED FORM) */}
            <div className="email-row">
              <input
                type="email"
                placeholder="Enter your personal or work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              <button
                type="button"
                className="btn-email"
                disabled={loading}
                onClick={handleContinueWithEmail}
              >
                Continue with email
              </button>
            </div>

            {/* Expand for password (login/signup switch) */}
            {(phase === "login" || phase === "signup") && (
              <div className="expand">
                <div className="pill-switch" role="tablist" aria-label="Choose login or signup">
                  <button
                    type="button"
                    className={`pill ${phase === "login" ? "active" : ""}`}
                    onClick={() => { setPhase("login"); setErr(""); }}
                    role="tab" aria-selected={phase === "login"}
                  >
                    I have a password
                  </button>
                  <button
                    type="button"
                    className={`pill ${phase === "signup" ? "active" : ""}`}
                    onClick={() => { setPhase("signup"); setErr(""); }}
                    role="tab" aria-selected={phase === "signup"}
                  >
                    I’m new here
                  </button>
                </div>

                {phase === "login" ? (
                  <form onSubmit={handleLogin} className="stack">
                    <input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                    <div className="row-between">
                      <a className="link" href="/reset-password">Forgot password?</a>
                    </div>
                    {err && <div className="error" role="alert">{err}</div>}
                    <button className="btn-primary" disabled={loading}>
                      {loading ? "Please wait…" : "Log in"}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleSignup} className="stack">
                    <input
                      type="password"
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      minLength={8}
                    />
                    <input
                      type="password"
                      placeholder="Confirm password"
                      value={password2}
                      onChange={(e) => setPassword2(e.target.value)}
                      autoComplete="new-password"
                      required
                      minLength={8}
                    />
                    <label className="agree">
                      <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} />
                      I agree to the <a href="/privacy-policy" className="link" target="_blank" rel="noreferrer">Privacy Policy</a> and{" "}
                      <a href="/terms-of-service" className="link" target="_blank" rel="noreferrer">Terms</a>.
                    </label>
                    {err && <div className="error" role="alert">{err}</div>}
                    <button className="btn-primary" disabled={loading}>
                      {loading ? "Please wait…" : "Create account"}
                    </button>
                  </form>
                )}
              </div>
            )}

            <p className="fine-print">
              By continuing, you acknowledge BloomPal’s <a className="link" href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
              You may receive occasional product updates and tips.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
